
import React from 'react';
import { X, Zap, Activity, ShieldAlert, Heart, Users, Sliders, Route, GitPullRequest, ArrowRightLeft, ShieldCheck, Microscope } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { HUDSlider, HUDButton } from './HUD/HUDPrimitives';
import { SIMULATION_TICK_RATE } from '../constants';

const AgentBehaviorModal: React.FC = () => {
  const { state, dispatch } = useAppContext();
  const { isBehaviorModalOpen, simConfig, pixelsPerMeter } = state;

  if (!isBehaviorModalOpen) return null;

  const ticksPerSecond = 1000 / SIMULATION_TICK_RATE;

  // Konvertavimo funkcijos
  const pxTickToMS = (pxTick: number) => (pxTick * ticksPerSecond) / pixelsPerMeter;
  const msToPxTick = (ms: number) => (ms * pixelsPerMeter) / ticksPerSecond;

  const updateConfig = (updates: Partial<typeof simConfig>) => {
    dispatch({ type: 'SET_SIM_CONFIG', payload: updates });
  };

  const Toggle = ({ active, onClick, label, desc }: { active: boolean, onClick: () => void, label: string, desc: string }) => (
    <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-white/5 group hover:border-cyan-500/30 transition-all">
      <div className="space-y-1">
        <span className="text-xs font-bold text-slate-100 uppercase tracking-wider">{label}</span>
        <p className="text-[10px] text-slate-500">{desc}</p>
      </div>
      <button 
        onClick={onClick}
        className={`w-12 h-6 rounded-full relative transition-colors ${active ? 'bg-cyan-500' : 'bg-slate-700'}`}
      >
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg transition-all ${active ? 'left-7' : 'left-1'}`}></div>
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md p-6 animate-in fade-in duration-300">
      <div className="w-full max-w-6xl bg-slate-950 border border-cyan-500/30 rounded-3xl shadow-[0_0_100px_rgba(6,182,212,0.15)] flex flex-col overflow-hidden max-h-[95vh]">
        
        <div className="p-8 border-b border-white/10 flex justify-between items-center bg-slate-900/50">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-cyan-500/20 rounded-xl border border-cyan-500/40">
              <Sliders className="text-cyan-400" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-rajdhani font-bold text-white tracking-widest uppercase text-glow text-cyan-400">
                Sistemos Branduolio Protokolai
              </h2>
              <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">Mastelis: {pixelsPerMeter.toFixed(1)} px/m // Taktai: {ticksPerSecond} Hz</p>
            </div>
          </div>
          <button onClick={() => dispatch({ type: 'SET_BEHAVIOR_MODAL_OPEN', payload: false })} className="p-3 hover:bg-white/5 rounded-full text-slate-400 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-10 space-y-12 custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Klinikinis eiliškumas */}
            <section className="space-y-6">
              <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-[0.3em] flex items-center gap-3">
                <ArrowRightLeft size={16}/> Klinikinis Kelias
              </h3>
              <div className="space-y-3">
                <Toggle 
                  label="Kritinių prioritetas" 
                  desc="Traumos praleidžia triažą"
                  active={simConfig.skipTriageForCritical} 
                  onClick={() => updateConfig({ skipTriageForCritical: !simConfig.skipTriageForCritical })}
                />
                <Toggle 
                  label="Diagnostika pirmiausia" 
                  desc="Tyrimai prieš gydytojo apžiūrą"
                  active={simConfig.diagnosticsFirst} 
                  onClick={() => updateConfig({ diagnosticsFirst: !simConfig.diagnosticsFirst })}
                />
                <Toggle 
                  label="Greitasis kanalas" 
                  desc="Lengvos traumos į procedūrinį"
                  active={simConfig.fastTrackTrauma} 
                  onClick={() => updateConfig({ fastTrackTrauma: !simConfig.fastTrackTrauma })}
                />
              </div>
            </section>

            {/* Saugumas ir Infekcijos */}
            <section className="space-y-6">
              <h3 className="text-sm font-bold text-red-400 uppercase tracking-[0.3em] flex items-center gap-3">
                <ShieldCheck size={16}/> Bio-Saugumas
              </h3>
              <div className="space-y-3">
                <Toggle 
                  label="Griežta izoliacija" 
                  desc="Infekuoti tiesiai į palatas"
                  active={simConfig.strictIsolationProtocol} 
                  onClick={() => updateConfig({ strictIsolationProtocol: !simConfig.strictIsolationProtocol })}
                />
                <Toggle 
                  label="Vengti taršos" 
                  desc="Dinaminis maršruto keitimas"
                  active={simConfig.avoidContamination} 
                  onClick={() => updateConfig({ avoidContamination: !simConfig.avoidContamination })}
                />
                <HUDSlider 
                  label="Užkrato spindulys" value={simConfig.infectionRadius} 
                  min={1} max={50} step={1} suffix=" m" color="red"
                  onChange={v => updateConfig({ infectionRadius: v })}
                />
              </div>
            </section>

            {/* Personalo valdymas */}
            <section className="space-y-6">
              <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-[0.3em] flex items-center gap-3">
                <Users size={16}/> Resursų Valdymas
              </h3>
              <div className="space-y-4 bg-white/5 p-5 rounded-2xl border border-white/5">
                <div className="space-y-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Aptarnavimo eilė</span>
                  <div className="flex bg-black/40 p-1 rounded-lg border border-white/10">
                    <button 
                      onClick={() => updateConfig({ staffPrioritization: 'ACUITY' })}
                      className={`flex-1 py-2 text-[10px] font-bold uppercase rounded transition-all ${simConfig.staffPrioritization === 'ACUITY' ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-900/40' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                      Sunkumas
                    </button>
                    <button 
                      onClick={() => updateConfig({ staffPrioritization: 'FIFO' })}
                      className={`flex-1 py-2 text-[10px] font-bold uppercase rounded transition-all ${simConfig.staffPrioritization === 'FIFO' ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-900/40' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                      Eilės tvarka
                    </button>
                  </div>
                </div>
                <HUDSlider 
                   label="Spūsčių suvokimas" 
                   value={simConfig.congestionAwareness * 100} 
                   min={0} max={100} step={10} 
                   suffix="%"
                   color="emerald"
                   onChange={v => updateConfig({ congestionAwareness: v / 100 })}
                />
              </div>
            </section>

          </div>

          {/* Antra eilė: Dinamika (Greitis metrais per sekundę) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
             <section className="space-y-6 bg-cyan-500/5 p-6 rounded-3xl border border-cyan-500/10">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-[0.3em] flex items-center gap-3">
                    <Zap size={16}/> Judėjimo Dinamika
                  </h3>
                  <span className="text-[10px] font-mono text-cyan-500/60 uppercase">Vienetai: metrai / sek</span>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <HUDSlider 
                      label="Pacientų greitis" 
                      value={pxTickToMS(simConfig.basePatientSpeed)} 
                      min={0.2} max={3.0} step={0.1} suffix=" m/s"
                      onChange={v => updateConfig({ basePatientSpeed: msToPxTick(v) })}
                    />
                    <p className="text-[9px] text-slate-500 font-mono">Standartas: ~1.2 - 1.4 m/s</p>
                  </div>
                  <div className="space-y-2">
                    <HUDSlider 
                      label="Personalo greitis" 
                      value={pxTickToMS(simConfig.baseStaffSpeed)} 
                      min={0.5} max={4.0} step={0.1} suffix=" m/s"
                      onChange={v => updateConfig({ baseStaffSpeed: msToPxTick(v) })}
                    />
                    <p className="text-[9px] text-slate-500 font-mono">Skubėjimas: ~1.6 - 2.5 m/s</p>
                  </div>
                </div>
             </section>
             <section className="space-y-6 bg-red-500/5 p-6 rounded-3xl border border-red-500/10">
                <h3 className="text-sm font-bold text-red-400 uppercase tracking-[0.3em] flex items-center gap-3">
                  <Microscope size={16}/> Sisteminiai Koeficientai
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <HUDSlider 
                    label="Kantrybės praradimas" value={simConfig.patienceDepletionRate} 
                    min={0.1} max={5} step={0.1} suffix="x" color="red"
                    onChange={v => updateConfig({ patienceDepletionRate: v })}
                  />
                  <HUDSlider 
                    label="Hospitalizacijos %" value={simConfig.admissionRate} 
                    min={0} max={100} step={1} suffix="%" color="red"
                    onChange={v => updateConfig({ admissionRate: v })}
                  />
                </div>
             </section>
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-white/10 bg-slate-900/80 flex justify-end gap-4">
          <button 
            onClick={() => dispatch({ type: 'SET_BEHAVIOR_MODAL_OPEN', payload: false })}
            className="px-10 py-3 text-xs font-bold uppercase text-slate-400 hover:text-white transition-colors font-mono"
          >
            ATŠAUKTI
          </button>
          <HUDButton 
            variant="cyan" 
            className="px-12 py-3"
            onClick={() => dispatch({ type: 'SET_BEHAVIOR_MODAL_OPEN', payload: false })}
          >
            ATNAUJINTI PROTOKOLĄ
          </HUDButton>
        </div>
      </div>
    </div>
  );
};

export default AgentBehaviorModal;
